/* iter369 P2 GAP-11 — Cross-border data transfer disclosure (SCCs / DPF).
 *
 * Public legal page describing EU → US data flows to LLM providers and
 * the legal basis under GDPR Chapter V (Art. 44-49).
 */
import React from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import { Globe, Shield, Mail, ExternalLink } from "lucide-react";

export default function DataTransfersPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const sections = bg
    ? [
        {
          title: "1. Какво пресича границата?",
          body: "Когато използвате AI обясненията на Plate Simulator, Recipe Vision, Voice Meal или Coach AI, ограничен подмножество от Вашите данни (имена на храни, метаболитни числа, езикова настройка) се изпраща към OpenAI (GPT-5.2, Whisper) или Google (Gemini 2.5 Pro). Тези API доставчици оперират сървъри в САЩ. Никога не изпращаме Вашия имейл, име, ICD-10 код, медикаменти или диагнози към LLM провайдерите.",
        },
        {
          title: "2. Правно основание (GDPR гл. V)",
          body: "Трансферът се извършва съгласно EU-U.S. Data Privacy Framework (DPF), официално признат от Комисията на ЕС с решение C(2023)4745 от 10 юли 2023 г. като осигуряващ адекватно ниво на защита. Допълнително поддържаме Standard Contractual Clauses (SCCs) на ЕК 2021/914 като резервен механизъм.",
        },
        {
          title: "3. Срок и минимизация",
          body: "Гласови записи и снимки на храна: 0 секунди постоянно съхранение — обработваме в RAM, изтриваме оригинала след извличане. Текстови запитвания към LLM: НЕ се персистират от наша страна. OpenAI / Google имат собствени политики за съхранение (виж openai.com/policies/eu-privacy и cloud.google.com/terms/data-processing-addendum).",
        },
        {
          title: "4. Вашите права",
          body: "Имате право да възразите срещу международния трансфер като оттеглите Вашето здравно съгласие (Профил → Поверителност → Оттегли здравното съгласие). Това ще деактивира AI обясненията, но платформата ще продължи да работи в базов рулебазиран режим. Можете и да изключите профилирането (GDPR чл. 22) без да оттегляте съгласието.",
        },
        {
          title: "5. Жалби",
          body: "За въпроси относно трансфера или подаване на жалба: dpo@nutramilo.com. Българският надзорен орган: Комисия за защита на личните данни (КЗЛД), София, ул. „Проф. Цветан Лазаров\" 2, www.cpdp.bg.",
        },
      ]
    : [
        {
          title: "1. What crosses the border?",
          body: "When you use AI explanations in Plate Simulator, Recipe Vision, Voice Meal or Coach AI, a limited subset of your data (food names, metabolic numbers, language setting) is sent to OpenAI (GPT-5.2, Whisper) or Google (Gemini 2.5 Pro). These API providers operate servers in the United States. We NEVER send your email, name, ICD-10 code, medications or diagnoses to LLM providers.",
        },
        {
          title: "2. Legal basis (GDPR Chapter V)",
          body: "Transfer occurs under the EU-U.S. Data Privacy Framework (DPF), formally recognized by the EU Commission via decision C(2023)4745 of July 10, 2023 as providing an adequate level of protection. Additionally we maintain Standard Contractual Clauses (SCCs) per Commission Implementing Decision 2021/914 as a fallback mechanism.",
        },
        {
          title: "3. Retention and minimization",
          body: "Voice recordings and food photos: 0 seconds persistent storage — processed in RAM, original deleted after extraction. Text queries to LLMs: NOT persisted on our side. OpenAI / Google have their own retention policies (see openai.com/policies/eu-privacy and cloud.google.com/terms/data-processing-addendum).",
        },
        {
          title: "4. Your rights",
          body: "You may object to the international transfer by revoking your health-data consent (Profile → Privacy → Revoke health-data consent). This will disable AI explanations, but the platform will continue to operate in basic rule-based mode. You can also opt out of profiling (GDPR Art. 22) without revoking consent.",
        },
        {
          title: "5. Complaints",
          body: "For transfer-related questions or to file a complaint: dpo@nutramilo.com. Bulgarian supervisory authority: Commission for Personal Data Protection (CPDP), Sofia, 2 Prof. Tsvetan Lazarov Blvd, www.cpdp.bg.",
        },
      ];

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,8%)] text-gray-100" : "bg-gray-50 text-gray-900"}`} data-testid="data-transfers-page">
      <SEO
        title={bg ? "Международен трансфер на данни — Nutramilo" : "International Data Transfer — Nutramilo"}
        description={bg
          ? "Прозрачно изясняване на EU↔US трансфер на данни към LLM провайдери под GDPR Глава V и EU-U.S. DPF."
          : "Transparent disclosure of EU↔US data transfers to LLM providers under GDPR Chapter V and EU-U.S. DPF."}
      />

      <header className={`border-b ${isDark ? "border-white/10 bg-[hsl(222,47%,10%)]" : "border-gray-200 bg-white"}`}>
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-7 h-7 text-emerald-500" />
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
              {bg ? "Международен трансфер на данни" : "International Data Transfer"}
            </h1>
          </div>
          <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg ? "GDPR глава V · EU-U.S. Data Privacy Framework · SCCs 2021/914" : "GDPR Chapter V · EU-U.S. Data Privacy Framework · SCCs 2021/914"}
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-5">
        {sections.map((s, i) => (
          <section key={i} className={`rounded-xl border p-5 ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}>
            <h2 className="text-base font-bold mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4 text-emerald-500" />
              {s.title}
            </h2>
            <p className={`text-[13px] leading-relaxed ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              {s.body}
            </p>
          </section>
        ))}

        <section className={`rounded-xl border p-5 ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`} data-testid="data-transfers-contacts">
          <h2 className="text-base font-bold mb-2">{bg ? "Контакти" : "Contacts"}</h2>
          <ul className="space-y-2 text-[13px]">
            <li className="flex items-center gap-2">
              <Mail className="w-4 h-4 opacity-70" />
              <a href="mailto:dpo@nutramilo.com" className="text-emerald-500 hover:underline" data-testid="link-dpo-email">dpo@nutramilo.com</a>
            </li>
            <li className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4 opacity-70" />
              <a href="https://www.cpdp.bg" target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:underline" data-testid="link-cpdp-transfers">
                www.cpdp.bg
              </a>
            </li>
            <li className="flex items-center gap-2">
              <ExternalLink className="w-4 h-4 opacity-70" />
              <a href="https://www.dataprivacyframework.gov" target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:underline" data-testid="link-dpf">
                www.dataprivacyframework.gov
              </a>
            </li>
          </ul>
        </section>
      </main>
    </div>
  );
}
